import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ComponentProviderProps } from '@business/interfaces/component-provider';
import { routePaths, adminRoutePaths } from '@utils/constants/routePaths';
import {
  getToken,
  setUserNotificationData,
  getSubscriberId
} from '@ports/common/sessionStateSlice';
import {
  getFirstLogin,
  getIsAuthenticated,
  getIsAdmin,
  getPermissions
} from '@ports/common/authStateSlice';
import { onMessage } from 'firebase/messaging';
import { messaging } from '@pushNotifications/firebase';
import socketService from '@business/services/SocketService';
import { dotenv } from '@utils/constants/env';
const authUrls = [
  '/',
  routePaths.SignUp,
  routePaths.SignIn,
  routePaths.ForgotPassword,
  routePaths.TwoFactorAuthentication
];

const publicUrls = [routePaths.PrivacyPolicy, routePaths.TermsAndConditions];

/*
 * This component is used redirect the user to corresponding page if user is logged in
 */
export function AuthGuard({ children }: Readonly<ComponentProviderProps>) {
  const dispatch = useDispatch();
  const storeToken = useSelector(getToken);
  const sessionToken = sessionStorage.getItem('token');
  const token = storeToken ? storeToken : sessionToken;

  const isAuthenticated = useSelector(getIsAuthenticated);
  const isFirstLogin = useSelector(getFirstLogin);
  const isAdmin = useSelector(getIsAdmin);
  const subscriberId = useSelector(getSubscriberId);
  const permissions = useSelector(getPermissions);

  const reactLocation = useLocation();
  const pathname = reactLocation.pathname;

  useEffect(() => {
    if (publicUrls.includes(pathname)) {
      return;
    }
    if (
      !token &&
      !authUrls.includes(pathname) &&
      !pathname.startsWith(routePaths.ResetPassword) &&
      !pathname.startsWith(routePaths.SetPasswordRequest)
    ) {
      window.location.href = '/';
    } else if (token && isAuthenticated) {
      const isAuthUrl = authUrls.includes(pathname);
      const isAdminUrl = pathname.startsWith(adminRoutePaths.AdminHome);

      if (isAdmin) {
        if (!isAdminUrl) {
          window.location.href = adminRoutePaths.AdminHome;
        }
      } else {
        if (isAdminUrl) {
          window.location.href = '/NotFound';
        } else if (isAuthUrl) {
          if (isFirstLogin) {
            window.location.href = routePaths.Subscription;
          } else if (permissions?.includes('owner_access:true')) {
            window.location.href = routePaths.Photos;
          } else if (permissions?.includes('sharing:view')) {
            window.location.href = routePaths.Sharing;
          }
        }
      }
    }
  }, [pathname, token, isAuthenticated, isFirstLogin, isAdmin, subscriberId]);

  const notificationMessageHandler = useCallback(
    (payload: any) => {
      console.log('📨 Foreground message received:', payload);
      if (document.visibilityState === 'visible') {
        dispatch(
          setUserNotificationData({
            page: payload?.data?.mediaSource ?? 'photos',
            notificationData: payload?.data
          })
        );
      }
    },
    [dispatch]
  );

  // Setup Firebase foreground message listener for authenticated users
  useEffect(() => {
    if (dotenv?.SOCKET_URL) {
      if (token && isAuthenticated) {
        // create socket connection
        socketService.connect();

        return () => {
          // cleanup socket connection
          socketService.disconnect();
        };
      }
      return undefined;
    } else {
      if (!token || !isAuthenticated || !messaging) return undefined;

      const unsubscribe = onMessage(messaging, notificationMessageHandler);
      return () => {
        // Cleanup listener on unmount or when user logs out
        if (typeof unsubscribe !== 'undefined') {
          unsubscribe();
        }
      };
    }
  }, [token, isAuthenticated]);

  return <>{children}</>;
}
